import React, { useState, useEffect, useCallback } from "react";
import { Group, Badge, ActionIcon, Text } from "@mantine/core";
import { MdRefresh } from "react-icons/md";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";
import { useMsal } from "@azure/msal-react";

import { getToken, useLogin } from "../../authConfig";
import { getPeakHoursAnalyticsApi, PeakHoursAnalyticsData } from "../../api";
import {
    ChartScaffold,
    DateRangeControl,
    CountryFilterControl,
    DomainFilterControl,
    TouchpointFilterControl,
    createDefaultDateRange,
    formatDateRange
} from "./shared";
import { TimezoneIndicator } from "../common/TimezoneIndicator";
import { TimezoneUtils } from "../../utils/timezoneUtils";

export interface PeakHoursChartProps {
    title?: string;
    height?: number;
    showTitle?: boolean;
    showLegend?: boolean;
    showStats?: boolean;
    entraOid?: string; // Filter for specific user
    defaultDaysBack?: number; // Number of days back for default date range
    colors?: {
        activity?: string;
        users?: string;
    };
}

export const PeakHoursChart: React.FC<PeakHoursChartProps> = ({
    title = "Peak Activity Hours",
    height = 400,
    showTitle = true,
    showLegend = true,
    showStats = true,
    entraOid,
    defaultDaysBack = 7,
    colors = {
        activity: "#228be6",
        users: "#51cf66"
    }
}) => {
    const { instance } = useMsal();
    const client = useLogin ? instance : undefined;
    const [data, setData] = useState<PeakHoursAnalyticsData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState(() => createDefaultDateRange(defaultDaysBack));
    const [availableCountries, setAvailableCountries] = useState<string[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [availableDomains, setAvailableDomains] = useState<string[]>([]);
    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
    const [availableTouchpoints, setAvailableTouchpoints] = useState<string[]>([]);
    const [selectedTouchpoints, setSelectedTouchpoints] = useState<string[]>([]);
    const [sqlQuery, setSqlQuery] = useState<string | undefined>();

    const fetchData = useCallback(async () => {
        if (!client) return;

        setLoading(true);
        setError(null);

        try {
            const token = await getToken(client);
            if (!token) {
                throw new Error("Failed to get access token");
            }

            // Get user timezone
            const userTimezone = TimezoneUtils.getUserTimezone();

            // Format dates as YYYY-MM-DD strings in user timezone
            const [start, end] = dateRange;
            const startDate = TimezoneUtils.formatDateForAPI(start);
            const endDate = TimezoneUtils.formatDateForAPI(end);
            const countries = selectedCountries.length > 0 ? selectedCountries : undefined;
            const domains = selectedDomains.length > 0 ? selectedDomains : undefined;
            const touchpoints = selectedTouchpoints.length > 0 ? selectedTouchpoints : undefined;

            const response = await getPeakHoursAnalyticsApi(token, startDate, endDate, userTimezone, countries, touchpoints, entraOid, domains);

            setData(response.data);
            setSqlQuery(response.sql_query);

            // Update available countries and touchpoints from the analytics response
            if (response.available_countries) {
                setAvailableCountries(response.available_countries);
            }
            setAvailableDomains(response.available_domains || []);
            if (response.available_touchpoints) {
                setAvailableTouchpoints(response.available_touchpoints);
            }
        } catch (err) {
            console.error("Error fetching peak hours analytics:", err);
            setError(err instanceof Error ? err.message : "Unknown error occurred");
            setSqlQuery(undefined);
        } finally {
            setLoading(false);
        }
    }, [client, dateRange, selectedCountries, selectedDomains, selectedTouchpoints, entraOid]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const formatXAxisTick = (tickValue: number): string => {
        const hour = tickValue.toString().padStart(2, "0");
        return `${hour}:00`;
    };

    const formatTooltipLabel = (value: unknown) => {
        if (typeof value === "number") {
            const hour = value.toString().padStart(2, "0");
            return `${hour}:00`;
        }

        const numericValue = Number(value ?? 0);
        if (Number.isFinite(numericValue)) {
            const hour = numericValue.toString().padStart(2, "0");
            return `${hour}:00`;
        }

        return `${value ?? ""}`;
    };

    const formatTooltipValue = (value: unknown) => {
        const scalar = Array.isArray(value) ? (value as (number | string)[])[0] : (value as number | string | undefined);
        if (typeof scalar === "number") {
            return [scalar, "Activities"];
        }

        const numericValue = Number(scalar ?? 0);
        return [Number.isFinite(numericValue) ? numericValue : (scalar ?? ""), "Activities"];
    };

    // Calculate stats for the scaffold
    const totalActivities = data.reduce((sum, d) => sum + d.activity_count, 0);
    const peakHour = data.reduce((max, current) => (current.activity_count > max.activity_count ? current : max), data[0] || { hour: 0, activity_count: 0 });

    const stats =
        showStats && totalActivities > 0 ? (
            <Group gap="xs">
                <Badge color="blue" variant="light" size="sm">
                    {totalActivities.toLocaleString()} Total Activities
                </Badge>
                <Badge color="green" variant="light" size="sm">
                    Peak: {peakHour ? formatXAxisTick(peakHour.hour) : "N/A"}
                </Badge>
            </Group>
        ) : null;

    const controls = (
        <Group gap={6} style={{ flexWrap: "nowrap" }}>
            <DateRangeControl value={dateRange} onChange={setDateRange} disabled={!!error} />
            <CountryFilterControl
                value={selectedCountries}
                onChange={setSelectedCountries}
                availableCountries={availableCountries}
                disabled={!!error}
                loading={loading}
            />
            <DomainFilterControl
                value={selectedDomains}
                onChange={setSelectedDomains}
                availableDomains={availableDomains}
                disabled={!!error}
                loading={loading}
            />
            <TouchpointFilterControl
                value={selectedTouchpoints}
                onChange={setSelectedTouchpoints}
                availableTouchpoints={availableTouchpoints}
                disabled={!!error}
                loading={loading}
            />
            <ActionIcon variant="light" size="sm" onClick={fetchData} loading={loading} title="Refresh data" disabled={!!error}>
                <MdRefresh size={16} />
            </ActionIcon>
        </Group>
    );

    // Check if any filters are applied (different from default state)
    const defaultDateRange = createDefaultDateRange(defaultDaysBack);
    const isDateRangeModified = dateRange[0].getTime() !== defaultDateRange[0].getTime() || dateRange[1].getTime() !== defaultDateRange[1].getTime();
    const hasFiltersApplied = isDateRangeModified || selectedCountries.length > 0 || selectedDomains.length > 0 || selectedTouchpoints.length > 0;

    const handleResetFilters = () => {
        setDateRange(createDefaultDateRange(defaultDaysBack));
        setSelectedCountries([]);
        setSelectedDomains([]);
        setSelectedTouchpoints([]);
    };

    const info = (
        <Group justify="flex-end" align="center" gap="xs">
            <Text size="xs" c="dimmed">
                {formatDateRange(dateRange)}
            </Text>
            <TimezoneIndicator size="xs" />
        </Group>
    );

    if (!useLogin) {
        return (
            <ChartScaffold title={title} loading={false} error="Authentication not configured">
                <Text>Login is not configured for this application.</Text>
            </ChartScaffold>
        );
    }

    return (
        <ChartScaffold
            title={title}
            showTitle={showTitle}
            loading={loading}
            error={error}
            empty={!error && data.length === 0}
            emptyMessage="No activity data found for the selected time range."
            stats={stats}
            controls={controls}
            info={info}
            height={height}
            onRefresh={fetchData}
            onResetFilters={handleResetFilters}
            showResetFilters={hasFiltersApplied}
            infoContent="Displays peak activity hours throughout the day. Monitor when users are most active to optimize resource allocation."
            sqlQuery={sqlQuery}
            chartData={data}
            showDeveloperTools={true}
        >
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="hour"
                        tickFormatter={formatXAxisTick}
                        tick={{ fontSize: 12 }}
                        angle={data.length > 10 ? -45 : 0}
                        textAnchor={data.length > 10 ? "end" : "middle"}
                        height={data.length > 10 ? 60 : 30}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                        labelFormatter={formatTooltipLabel}
                        formatter={formatTooltipValue}
                        contentStyle={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 4 }}
                    />
                    {showLegend && <Legend />}
                    <Bar dataKey="activity_count" fill={colors.activity} name="Activities" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="activity_count" position="top" style={{ fontSize: "12px", fontWeight: 600 }} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </ChartScaffold>
    );
};
